import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
    Building2,
    Cpu,
    Database,
    LayoutTemplate,
    LineChart,
    ShieldCheck,
    Users,
    Target,
    Trophy,
    ArrowRight,
    FileText,
    Phone,
    ChevronDown
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import PreSignDialog from '../PreSign/PreSignDialog';
import ContactFormModal from '../ContactForm/ContactFormModal';

const Solutions = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(false);
    const [openContactForm, setOpenContactForm] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        try {
            ReactGA.send({ hitType: 'pageview', page: location.pathname || '/solutions', title: 'Solutions' });
        } catch { }

        // Override global scroll behavior
        const html = document.documentElement;
        const body = document.body;
        const originalHtmlScrollBehavior = html.style.scrollBehavior;
        const originalBodyScrollBehavior = body.style.scrollBehavior;

        html.style.scrollBehavior = 'auto';
        body.style.scrollBehavior = 'auto';

        return () => {
            html.style.scrollBehavior = originalHtmlScrollBehavior;
            body.style.scrollBehavior = originalBodyScrollBehavior;
        };
    }, [location.pathname]);

    const personas = [
        {
            icon: Users,
            title: 'For Players',
            challenge: 'Your challenge today',
            challengeText: 'Training without clear feedback on what to improve',
            solution: 'How VISIST.AI helps',
            solutionText: 'Turn every training clip into a personal coach. Upload practice or match videos from your phone and receive AI-driven feedback on posture, timing, and shot execution, so you know exactly what to fix next session.'
        },
        {
            icon: Target,
            title: 'For Coaches',
            challenge: 'Your challenge today',
            challengeText: 'Difficulty tracking player progress and designing targeted drills',
            solution: 'How VISIST.AI helps',
            solutionText: 'Stop guessing and start measuring. Use standardized Smart Assessments and match breakdowns to design targeted drills, track improvement, and communicate clearly with players and parents.'
        },
        {
            icon: Building2,
            title: 'For Academies & Owners',
            challenge: 'Your challenge today',
            challengeText: 'Scaling quality training across multiple centers and coaches',
            solution: 'How VISIST.AI helps',
            solutionText: 'Run your academy like a data-driven program. VISIST.AI unifies assessments, reports, and coach inputs into a single view, helping you scale quality across multiple centers and increase retention.'
        },
        {
            icon: Trophy,
            title: 'For Tournaments & Leagues',
            challenge: 'Your challenge today',
            challengeText: 'Limited insights and engagement for grassroots events',
            solution: 'How VISIST.AI helps',
            solutionText: 'Bring broadcast-grade insights to grassroots events. Automate match tagging, generate highlight-worthy clips, and share performance summaries with players, coaches, and fans.'
        }
    ];

    const solutions = [
        {
            title: "Academy Digital Transformation & AI Integration",
            description: "We help traditional academies in transitioning from manual processes to digital workflows.",
            icon: Cpu,
            color: "text-emerald",
            gradient: "from-emerald/20 to-emerald/5"
        },
        {
            title: "Standardized Skill Assessment Framework Design",
            description: "Using Smart Assessments, the team can help sports organizations design standardized evaluation criteria across multiple venues.",
            icon: ShieldCheck,
            color: "text-blue-500",
            gradient: "from-blue-500/20 to-blue-500/5"
        },
        {
            title: "Performance Auditing",
            description: "Utilizing our core sports models built on Human Pose and Biomechanics, VISIST.AI can offer deep-dive technical audits for individual elite or para-athletes.",
            icon: LineChart,
            color: "text-purple-500",
            gradient: "from-purple-500/20 to-purple-500/5"
        },
        {
            title: '"Inference as a Service" Implementation',
            description: "If you are a sports or fitness app developer, we can enable you to pull AI-driven insights—such as shuttle tracking or foot fault detection—into your own applications.",
            icon: Database,
            color: "text-amber-500",
            gradient: "from-amber-500/20 to-amber-500/5"
        },
        {
            title: "Custom AI/CV Product Architecture Consulting",
            description: "The team comes with a vast experience in Video systems, IOT, AI, Large Data Systems management you can be rest assured that the technology decisions are in good hands.",
            icon: Building2,
            color: "text-rose-500",
            gradient: "from-rose-500/20 to-rose-500/5"
        }
    ];

    return (
        <div className="min-h-screen bg-[#050505] selection:bg-emerald/30 selection:text-emerald font-sans">

            {/* Hero Section - Always Dark */}
            <section className="relative h-screen flex items-center pt-20 overflow-hidden bg-[#050505]">
                {/* Abstract Architect Visual Background */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="/assets/generated/solutions_hero.png"
                        alt="Digital Architect Background"
                        className="w-full h-full object-cover opacity-60"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-transparent" />
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-4xl"
                    >

                        <h1 className="text-5xl md:text-7xl font-black text-white mb-8 leading-[1.1] tracking-tight uppercase">
                            Different roles, <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald via-neon-lime to-emerald">
                                one platform.
                            </span>
                        </h1>

                        <p className="text-xl md:text-2xl text-gray-300 font-light leading-relaxed mb-6 border-l-4 border-emerald pl-6">
                            VISIST.AI adapts to how you train, coach, and run your sport.
                        </p>

                        <div className="flex gap-3 pt-4">
                            <button
                                onClick={() => setOpenContactForm(true)}
                                className="px-4 py-2.5 md:px-8 md:py-4 bg-emerald hover:bg-emerald-600 text-white font-bold text-sm md:text-lg rounded-full transition-all transform hover:scale-105 shadow-[0_0_20px_rgba(16,185,129,0.3)] whitespace-nowrap flex items-center gap-2"
                            >
                                <Phone className="w-5 h-5" />
                                Let's Talk
                            </button>
                            <button
                                onClick={() => navigate('/case-studies')}
                                className="px-4 py-2.5 md:px-8 md:py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm md:text-lg rounded-full transition-all backdrop-blur-md whitespace-nowrap"
                            >
                                Case Studies
                            </button>
                        </div>
                    </motion.div>
                </div>

                {/* Scroll Down Indicator - Bottom Center */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2, duration: 0.8 }}
                    className="absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer group flex flex-col items-center gap-2 z-20"
                    onClick={() => {
                        const personasSection = document.querySelector('section.bg-white');
                        if (personasSection) {
                            personasSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    }}
                >
                    <span className="text-gray-400 text-sm uppercase tracking-[0.3em] font-light group-hover:text-emerald transition-colors duration-300">
                        SCROLL
                    </span>
                    <motion.div
                        animate={{
                            y: [0, 8, 0],
                        }}
                        transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    >
                        <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-emerald transition-colors duration-300" strokeWidth={1.5} />
                    </motion.div>
                </motion.div>
            </section>

            {/* Personas Grid - Premium Cards - WHITE BACKGROUND */}
            <section className="py-16 bg-white relative overflow-hidden">
                {/* Background Decoration */}
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-50 rounded-full blur-3xl opacity-40 -translate-y-1/2 translate-x-1/2"></div>

                <div className="container mx-auto px-6 relative z-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto">
                        {personas.map((persona, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.7, delay: index * 0.15, type: "spring", stiffness: 100 }}
                                className="group relative"
                            >
                                {/* Card */}
                                <div className="relative bg-white rounded-3xl p-10 border-2 border-gray-100 hover:border-emerald-200 transition-all duration-500 hover:shadow-[0_20px_60px_-15px_rgba(16,185,129,0.3)] h-full flex flex-col">
                                    {/* Top Accent Line */}
                                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                    {/* Icon Container */}
                                    <div className="mb-8">
                                        <div
                                            className="inline-flex p-5 rounded-2xl transition-all duration-500 group-hover:scale-110 group-hover:shadow-lg"
                                            style={{
                                                background: 'linear-gradient(135deg, #026b33 0%, #028a42 100%)'
                                            }}
                                        >
                                            <persona.icon className="w-6 h-6 text-white" strokeWidth={2} />
                                        </div>
                                    </div>

                                    {/* Title */}
                                    <h3 className="text-3xl md:text-4xl font-black text-slate-900 mb-8 tracking-tight">
                                        {persona.title}
                                    </h3>

                                    {/* Challenge */}
                                    <div className="mb-6">
                                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                                            {persona.challenge}
                                        </p>
                                        <p className="text-gray-600 font-medium leading-relaxed">
                                            {persona.challengeText}
                                        </p>
                                    </div>

                                    {/* Solution */}
                                    <div className="mb-8 flex-grow">
                                        <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#026b33' }}>
                                            {persona.solution}
                                        </p>
                                        <p className="text-gray-800 leading-relaxed text-lg">
                                            {persona.solutionText}
                                        </p>
                                    </div>

                                    {/* CTA */}
                                    <div className="mt-auto">
                                        <button
                                            onClick={() => navigate('/products')}
                                            className="group/btn w-full px-4 py-2.5 text-white font-bold rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-105 flex items-center justify-center gap-2 relative overflow-hidden"
                                            style={{
                                                background: 'linear-gradient(135deg, #026b33 0%, #028a42 100%)'
                                            }}
                                        >
                                            <span className="relative z-10">Explore Products</span>
                                            <ArrowRight className="w-5 h-5 relative z-10 group-hover/btn:translate-x-1 transition-transform" />
                                            {/* Shine effect */}
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700"></div>
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Custom Sports Analytics Solutions - DARK THEME */}
            <section id="solutions-section" className="py-32 bg-[#050505] relative overflow-hidden">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald/5 rounded-full blur-[100px] pointer-events-none" />

                <div className="container mx-auto px-6 relative z-10">
                    <div className="mb-20">
                        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Custom Sports Analytics Solutions</h2>
                        <p className="text-gray-400 text-xl max-w-4xl">
                            Consulting Solutions around bringing AI & CV solutions - whether you are a sports academy, an education institute having an active sports curriculum, a sports coach, a physical education teacher, a Khelo India fitness assessor, etc we have something for you.
                        </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-8 max-w-7xl mx-auto">
                        {solutions.map((sol, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0.95 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1, duration: 0.5 }}
                                viewport={{ once: true }}
                                className="group p-8 rounded-[2rem] bg-[#050505] border border-white/10 hover:border-white/20 relative overflow-hidden min-h-[400px] flex flex-col w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.34rem)]"
                            >
                                {/* Hover Glow */}
                                <div className={`absolute -inset-1 bg-gradient-to-br ${sol.gradient} opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500`} />
                                <div className="absolute inset-0 bg-[#050505]/90 m-[1px] rounded-[2rem] z-0" />

                                <div className="relative z-10 flex flex-col h-full">
                                    <div className={`w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center mb-6 text-white group-hover:scale-110 transition-transform duration-300 border border-white/5 group-hover:border-white/20`}>
                                        <sol.icon className={sol.color} size={28} />
                                    </div>

                                    <h3 className="text-xl font-bold text-white mb-4 group-hover:text-emerald transition-colors duration-300">{sol.title}</h3>
                                    <p className="text-gray-400 leading-relaxed flex-grow text-sm">
                                        {sol.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA Section - WHITE THEME */}
            <section className="py-12 relative bg-white border-t border-gray-100">
                <div className="container mx-auto px-6">
                    {/* Final CTA - World Class Design */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, type: "spring" }}
                        className="max-w-4xl mx-auto relative"
                    >
                        {/* Decorative Background Elements */}
                        <div className="absolute -top-24 -left-24 w-64 h-64 bg-emerald-200/30 rounded-full blur-3xl" />
                        <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-blue-200/30 rounded-full blur-3xl" />

                        {/* Main CTA Card */}
                        <div className="relative rounded-3xl bg-gradient-to-br from-gray-50 to-white p-8 md:p-12 shadow-xl border border-gray-100">
                            {/* Animated gradient border effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/0 via-emerald-400/20 to-emerald-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                            {/* Top accent line */}
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-emerald-400 to-transparent" />

                            <div className="relative z-10 text-center">
                                {/* Badge */}
                                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald/10 border border-emerald/20 mb-4">
                                    <span className="text-emerald font-bold text-xs uppercase tracking-wider">Ready to Transform?</span>
                                </div>

                                {/* Headline */}
                                <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3 leading-tight">
                                    Interested in bringing <br className="hidden md:block" />
                                    <span style={{ color: '#026b33' }}>VISIST.AI</span> to your <br className="hidden md:block" />
                                    academy or league?
                                </h2>

                                {/* CTA Button */}
                                <button
                                    onClick={() => setOpenContactForm(true)}
                                    className="group/btn relative px-8 py-3 md:px-10 md:py-4 text-white font-bold text-base md:text-lg rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl overflow-hidden"
                                    style={{
                                        background: 'linear-gradient(to right, #026b33, #028a42)',
                                        boxShadow: '0 20px 25px -5px rgba(2, 107, 51, 0.25), 0 10px 10px -5px rgba(2, 107, 51, 0.1)'
                                    }}
                                >
                                    <span className="relative z-10 flex items-center gap-3 justify-center">
                                        Let's talk
                                        <svg className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                        </svg>
                                    </span>
                                    {/* Button shine effect */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700" />
                                </button>

                                {/* Trust indicators */}
                                <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
                                    <div className="flex items-center gap-2">
                                        <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <span className="font-medium">Free Consultation</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <span className="font-medium">Custom Solutions</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <span className="font-medium">Expert Support</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* PreSign Dialog */}
            <PreSignDialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
            />

            {/* Contact Form Modal */}
            <ContactFormModal
                isOpen={openContactForm}
                onClose={() => setOpenContactForm(false)}
            />

        </div>
    );
};

export default Solutions;
